import {AfterViewChecked, AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AccountEvent} from '../../models/AccountEvent';
import {combineLatest, Subscription} from 'rxjs';
import {AppState} from '../../models/AppState';
import {FireService} from '../../services/fire.service';
import {AppStateService} from '../../services/app-state.service';
import {Router} from '@angular/router';
import {TranslateService} from '../../services/translate.service';
import {AuthService} from '../../services/auth.service';
import {AuthorizedApiService} from '../../services/authorized-api.service';
import moment from 'moment';
import {DraggerComponent} from '../ui/dragger.component';
import {NgClass, NgForOf, NgIf} from '@angular/common';
import {ViewportService} from '../../services/viewport-service';
import {PlainButtonComponent} from '../buttons/plain-button.component';
import {OrderPageNavComponent} from '../ui/order-page-nav.component';
import {CircleButtonComponent} from '../buttons/circle-button.component';
import {UrlHistoryService} from '../../services/url.history.service';
import {ActionButtonComponent} from '../buttons/action-button.component';
import {SquareButtonComponent} from '../buttons/square-button.component';
import {CallWaitressDialogComponent} from '../dialogs/call-waitress-dialog.component';
import {ModalService} from '../../services/modal.service';
import {LinkButtonComponent} from '../buttons/link-button.component';
import {WebCodeModalComponent} from '../dialogs/web-code-dialog.component';
import MoneyUtils, {MenuUtils} from '../../utils/Utils';
import { RoomCodeModalComponent } from '../dialogs/room-code-dialog.component';
import {LightIconButtonComponent} from '../buttons/light-icon-button-component';
import {PageButtonComponent} from '../buttons/page-button.component';
import {NotificationService} from '../../services/notification.service';
import {StarRatingComponent} from '../ui/star-rating.component';
import {NavigationEnd} from '@angular/router';
import {ReviewService} from '../../services/review.service';
import {BottomSheetService} from '../../services/bottom-sheet.service';
import {VoucherService} from '../../services/voucher.service';
import {FSVoucher} from '../../models/FSVoucher';
import {ReviewState} from '../../models/Order';

@Component({
  selector: 'app-modern-feed',
  standalone: true,
  imports: [
    DraggerComponent,
    NgForOf,
    NgIf,
    PlainButtonComponent,
    OrderPageNavComponent,
    CircleButtonComponent,
    NgClass,
    ActionButtonComponent,
    SquareButtonComponent,
    LinkButtonComponent,
    LightIconButtonComponent,
    PageButtonComponent,
    StarRatingComponent
  ],
  templateUrl: './modern-feed.component.html',
  styleUrl: './feed.component.css'
})
export class ModernFeedComponent implements OnInit, OnDestroy, AfterViewChecked {

  @ViewChild("feedScroller") private feedContainer!: ElementRef;
  events?: AccountEvent[];
  private eventSub?: Subscription;
  private appStateSub?: Subscription;
  private sub = new Subscription();
  private currentUserId?: number;
  private vouchersSub?: Subscription;
  private vouchersForPriceOverride: FSVoucher[] = [];
  vouchers = [{ consumed: false, name: "Test" }];
  appState?: AppState;
  private largestSC = 0;
  private clickCount = 0;
  showConsole = false;
  message?: string;
  showLatest = true;
  show6Hour = false;
  hasOlderEvents = false;
  private scrollTimeout: any;
  private allEvents?: AccountEvent[];
  title?: string;
  inhouse?: boolean;
  roomservice?: boolean;
  showCallForWaitress?: boolean;
  isOpenTable: boolean = true;
  isKioskMode: boolean = false;
  showReview = false;
  showThankYouState = false;
  prebookBooked?: { booking_ref: string };
  // The number the guest knows from the source booking system
  prebookExternalRef?: string;
  private currentReceiptKey: string | null = null;
  private reviewFetchDone = false;
  private reviewSheetObserverSetup = false;
  private currentReviewState: ReviewState | null = null;

  constructor(private fire: FireService, private appStateService: AppStateService, private router: Router,
              private api: AuthorizedApiService, private auth: AuthService, public translate: TranslateService,
              private viewportService: ViewportService, private urlHistory: UrlHistoryService,
              private modalService: ModalService, private notService: NotificationService,
              public reviewService: ReviewService, private sheetService: BottomSheetService,
              private voucherService: VoucherService) { }

  ngOnInit(): void {
    this.appStateSub = this.appStateService.observeAppState().subscribe(appState => {
      this.appState = appState;
      if (!this.vouchersSub && MoneyUtils.hasVoucherPriceOverrides(appState.venueId)) {
        this.vouchersSub = combineLatest([
          this.voucherService.observeActiveVouchers(),
          this.voucherService.observeInactiveVouchers()
        ]).subscribe(([active, inactive]) => this.vouchersForPriceOverride = [...(active ?? []), ...(inactive ?? [])]);
      }
      this.currentReviewState = appState.order?.review_state ?? null;
      this.updateStates(appState);
      const userHasChanged = this.checkUserChanged(appState.order.user_cfg.user_id!);
      if (!this.eventSub || userHasChanged) {
        this.currentUserId = appState.order.user_cfg.user_id;
        this.beginObserving(appState);
      }
      this.updateReviewVisibility();
    });
  }

  private updateStates(appState: AppState) {
    if (appState.order == null || appState.order.user_cfg == null) {
      return;
    }

    const dtcfg = MenuUtils.getDeliverTypeConfig(appState?.order?.user_cfg?.dt, appState.cfg);
    this.title = MenuUtils.getDTSubTitle(appState, dtcfg, this.translate) ?? appState.cfg.name;
    this.inhouse = appState.order.user_cfg.dt === "table";
    // With one room per table the room comes from the scanned code and can't be changed
    this.roomservice = appState.order.user_cfg.dt === "roomservice" && !appState.cfg.dt?.roomservice?.room_per_table;
    this.isOpenTable = appState.order.user_cfg.payment?.open_order ?? false;
    this.isKioskMode = this.auth.isKioskMode();

    // Prebook context: a completed prebooking shows a confirmation card with a
    // link to the booking view. The dt title ("Takeaway") and the open-tab pay
    // button do not apply in prebook mode — the booking is paid at the venue.
    this.prebookBooked = appState.order.prebook?.booked;
    this.prebookExternalRef = appState.order.prebook?.prebooking?.external_ref;
    if (appState.order.prebook) {
      this.title = appState.cfg.name;
      this.isOpenTable = false;
    }

    if (dtcfg) {
      const kioskState = this.auth.getKioskState();
      const isMonterMode = this.auth.isKioskMode() && kioskState === "monter";
      this.showCallForWaitress = dtcfg.open && dtcfg.call_waiter && appState.order.user_cfg.table != null && !isMonterMode;
    }

    this.reviewService.loadConfig(appState.cfg);
  }

  private setupReviewSheetObserver() {
    if (this.reviewSheetObserverSetup) return;
    this.reviewSheetObserverSetup = true;
    let wasReviewDetailOpen = false;
    this.sub.add(this.sheetService.observeActiveSheet().subscribe(sheet => {
      if (sheet?.sheetClass === 'review-detail') {
        wasReviewDetailOpen = true;
      } else if (wasReviewDetailOpen && !sheet) {
        wasReviewDetailOpen = false;
        if (!this.currentReceiptKey) return;
        this.reviewService.fetchAndApplyReview(this.currentReceiptKey).subscribe(() => {
          this.updateReviewVisibility();
        });
      }
    }));
  }

  onOverallRatingSelected(rating: number) {
    this.reviewService.overallRating = rating;
    this.reviewService.reviewSubmitted = true;
    this.updateReviewVisibility();

    if (this.currentReceiptKey && !this.reviewService.saveDisabled) {
      this.reviewService.submitCurrentReview(this.currentReceiptKey, 'self_service', rating).subscribe({
        next: resp => {
          console.log('Review submitted');
          this.currentReviewState = resp.review_state ?? null;
        },
        error: (e: any) => {
          console.warn('Failed to submit review:', e);
          if (e?.status === 403) {
            this.reviewService.saveDisabled = true;
          }
        }
      });
    }

    if (!this.reviewService.hasSubReviews && !this.reviewService.canShowGoogleReview(rating)) return;

    this.sheetService.displaySheetClass('review-detail', this.translate.getString('Betygsätt ditt besök'), {
      overallRating: rating,
      type: 'self_service',
      receiptKey: this.currentReceiptKey,
      ratingFood: this.reviewService.ratingFood,
      ratingService: this.reviewService.ratingService,
      ratingAmbiance: this.reviewService.ratingAmbiance,
      ratingValue: this.reviewService.ratingValue,
      subReviews: this.reviewService.subReviews
    });
  }

  private updateReviewVisibility() {
    const receiptKey = this.findLatestReceiptKey();
    const dt = this.appState?.order?.user_cfg?.dt;
    const excludedTypes = ['takeaway', 'pickup', 'foodcourt'];
    if ((dt && excludedTypes.includes(dt)) || !this.isOpenTable || !this.reviewService.enabled || this.reviewService.locked) {
      this.showReview = false;
      this.showThankYouState = false;
      return;
    }

    if (!receiptKey) {
      this.currentReceiptKey = null;
      this.reviewFetchDone = false;
      this.reviewService.resetRatings();
      this.showReview = false;
      this.showThankYouState = false;
      return;
    }

    if (receiptKey !== this.currentReceiptKey) {
      this.currentReceiptKey = receiptKey;
      this.reviewFetchDone = false;
      this.reviewService.resetRatings();
      this.showReview = false;
      this.showThankYouState = false;
      this.setupReviewSheetObserver();
    }

    if (this.reviewService.reviewSubmitted) {
      this.showReview = !this.reviewService.locked;
      this.showThankYouState = false;
      return;
    }

    // Evaluate gating before fetching — otherwise the stars briefly flash before
    // gating switches to Google-CTA or hides them.
    const gating = this.reviewService.evaluateGating(this.currentReviewState);
    if (gating.hide) {
      this.showReview = false;
      this.showThankYouState = false;
      return;
    }
    if (gating.showThankYou) {
      this.showReview = true;
      this.showThankYouState = true;
      return;
    }

    if (!this.reviewFetchDone) {
      const fetchKey = receiptKey;
      this.reviewService.fetchAndApplyReview(fetchKey).subscribe(() => {
        this.reviewFetchDone = true;
        if (this.currentReceiptKey !== fetchKey) return;
        this.updateReviewVisibility();
      });
      return;
    }

    this.showReview = !this.reviewService.locked;
    this.showThankYouState = false;
  }

  openGoogleReview() {
    const url = this.reviewService.getGoogleReviewUrl();
    if (url) window.open(url, '_blank');
  }

  private findLatestReceiptKey(): string | null {
    if (!this.events) return null;
    // Search backwards for a receipt, but if we find an order event first it means
    // the user placed a new order after the last receipt — don't show review
    for (let i = this.events.length - 1; i >= 0; i--) {
      const event = this.events[i];
      if (event.content?.order_items) return null;
      if (event.buttons) {
        for (const btn of event.buttons) {
          const match = btn.url?.match(/\/receipt\/([^/]+)/);
          if (match) return match[1];
        }
      }
    }
    return null;
  }

  private checkUserChanged(userId: number): boolean {
    if (!userId || !this.currentUserId) { return false; }
    return this.currentUserId !== userId;
  }

  private beginObserving(appState: AppState) {
    console.log("FeedComponent beginObserving");

    const kioskState = this.auth.getKioskState();
    const isKioskMode = this.auth.isKioskMode();
    console.log(`KioskMode: ${isKioskMode}, KioskState: ${kioskState}`);

    // Handle different modes
    if (isKioskMode && kioskState === "monter") {
      console.log("MonterMode: Hiding events");
      this.events = [];
      return;
    }

    // Standard mode (kiosk with 30min filter) or normal mode (all events)
    const source = isKioskMode ?
      this.fire.observeEventsWindow(appState.order.user_cfg.chat_id!, moment().subtract(30, "minute").toDate()) :
      this.fire.observeEvents(appState.order.user_cfg.chat_id!);

    this.eventSub?.unsubscribe();
    this.eventSub = source.subscribe(allEvents => {
      this.allEvents = allEvents;
      this.events = this.processEvents(allEvents, this.showLatest, this.show6Hour);
      this.notService.markRead();
      this.updateReviewVisibility();
    });
  }

  private processEvents(allEvents: AccountEvent[], filterAndKeepLatest: boolean, filterAndKeep6Hours: boolean): AccountEvent[] {
    let previousTime: moment.Moment | undefined;

    // Process events and detect time gaps
    const processedEvents = allEvents.map(event => {
      const time = moment(event.date!.toDate());

      // Format time string
      const hours = moment().diff(time, "hours");
      event.moment = time;
      event.dateString = hours > 24 ? time.format('YYYY-MM-DD') : undefined;
      event.timeString = time.format('HH:mm');

      // Parse body if exists
      if (event.body) {
        const body = JSON.parse(event.body);
        event.buttons = body.buttons;
        event.url = body.url;
        event.vouchers = body.vouchers;
      }
      if (event.content_json) {
        event.content = JSON.parse(event.content_json);
        if (event.content?.title)
          event.title = event.content.title;
      }

      // Detect if this event starts a new time group (more than 2 minutes from previous event)
      const isGroupStart = !previousTime || time.diff(previousTime, "seconds") > 120;
      previousTime = time;

      return {...event, isGroupStart};
    });

    if (filterAndKeep6Hours) {
      const sixHoursAgo = moment().subtract(6, 'hours');
      const filteredEvents = processedEvents.filter(event => event.moment.isAfter(sixHoursAgo));
      this.hasOlderEvents = filteredEvents.length < processedEvents.length;
      return filteredEvents;
    }

    if (filterAndKeepLatest) {
      // Find the last group start and filter events
      const lastGroupStartIndex = [...processedEvents]
        .reverse()
        .findIndex(e => e.isGroupStart);

      this.hasOlderEvents = lastGroupStartIndex !== -1;
      const evnts = lastGroupStartIndex === -1
        ? processedEvents
        : processedEvents.slice(processedEvents.length - lastGroupStartIndex - 1);
      return evnts;
    }

    return processedEvents;
  }

  ngOnDestroy(): void {
    clearTimeout(this.scrollTimeout);
    this.eventSub?.unsubscribe();
    this.appStateSub?.unsubscribe();
    this.vouchersSub?.unsubscribe();
    this.sub.unsubscribe();
  }

  ngAfterViewChecked(): void {
    // Debounce multiple rapid calls
    if (this.showLatest) {
      return;
    }
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      const sc = this.viewportService.scrollHeight();
      if (sc > this.largestSC) {
        this.largestSC = this.viewportService.scrollToBottom();
      }
    }, 50);
  }

  clicked(btn: any) {
    console.log(btn);
    this.router.navigateByUrl(btn.url);
  }

  bgClick() {
    this.clickCount++;
    if (this.clickCount % 10 === 0) {
      this.message = "";
      this.showConsole = true;
    }
  }

  go() {
    this.api.sendMessage(this.message!).subscribe(res => {
      this.showConsole = false;
    });
  }

  getString(s: string) {
    return this.translate.single(s);
  }

  getFormattedPrice(p: number, voucherId: number): string {
    const venueId = this.appState?.venueId;
    if (!MoneyUtils.hasVoucherPriceOverrides(venueId)) {
      return MoneyUtils.formatCent(p, true);
    }
    const voucher = this.vouchersForPriceOverride.find(v => v.voucher_id === String(voucherId));
    return MoneyUtils.formatCent(voucher ? MoneyUtils.voucherPriceCent(venueId, voucher) : p, true);
  }

  translateTitle(s: string) {
    if (this.translate.lang === "sv") { return s; }
    if (s.startsWith("Din beställning")) {
      //Din beställning<br>1x Tisdagspizza - 105 kr
      const suff = s.substring(19);
      const lines = suff.split("<br>");
      if (lines.length > 0) {
        const trslLines = [];
        for (const line of lines) {
          //&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Lax marinerad i soya, Kryddig riskaka
          if (line.startsWith("&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;")) {
            trslLines.push("&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"+this.getString(line.substring(30)));
            continue;
          }
          if (line.startsWith("Totalt:")) {
            trslLines.push(this.getString("Totalt") + ":" + line.substring(7));
            continue;
          }
          //1x Glassbåt med hemmagjord sylt - 260 kr
          const spi = line.indexOf(" ");
          if (spi > 0) {
            const pre = line.substring(0,spi+1);
            const post = line.substring(spi+1);
            const namePrice = post.split(" - ");
            trslLines.push(pre + " " + this.getString(namePrice[0]) + " - " + namePrice[1]);
          }
        }
        return this.getString("Din beställning") + "<br>" + trslLines.join("<br>");
      }
      return this.getString("Din beställning") + "<br>" + suff;
    } else if(s.startsWith("Tack för din beställning.")) {
      return this.getString("Tack för din beställning.") + " " + this.getString("Vi kommer strax ut med den.");
    } else if(s.startsWith("Notan är betald. Beställningsnummer")) {
      //Notan är betald. Beställningsnummer #69
      const parts = s.split(" #");
      return this.getString("Notan är betald. Beställningsnummer") + " #" + parts[1];
    } else if(s.startsWith("Wow")) {
      const s1 = this.getString("Wow! 😍 😍");
      if (s.includes("och övrig personal")) {
        //"Wow Anonym! 😍 😍<br><br>Davido, Lisa, Ronaldo och övrig personal gör allt för att du skall ha en trevlig upplevelse, så stort tack för dricksen!<br><br>Hoppas att allt var till din belåtenhet."
        const s2 = this.getString("och övrig personal gör allt för att du skall ha en trevlig upplevelse, så stort tack för dricksen!");
        const s3 = this.getString("Hoppas att allt var till din belåtenhet.");
        let names = "";
        if (s.includes("<br><br>")) {
          const d1 = s.split("<br><br>");
          const d2 = d1[1].split("och");
          names = d2[0];
        }
        return s1 + "<br><br>" + names + s2 + "<br><br>" + s3;
      } else if (s.includes("kommer strax ut med din beställning")) {
        //"Wow Anonym! 😍 😍 Tack för dricksen!<br><br>Robert kommer strax ut med din beställning."
        const s2 = this.getString("Tack för dricksen!");
        const s3 = this.getString("kommer strax ut med din beställning.");
        let names = "";
        if (s.includes("<br><br>")) {
          const d1 = s.split("<br><br>");
          const d2 = d1[1].split("kommer");
          names = d2[0];
        }
        return s1 + " " + s2 + "<br><br>" + names + s3;
      }
    } else if(s.startsWith("Tack") && s.endsWith("belåtenhet.")) {
      const s1 = this.getString("Tack!");
      const s3 = this.getString("Hoppas att allt var till din belåtenhet.");
      return s1 + " " + s3;
    }
    return this.getString(s);
  }

  translateSubTitle(s: string) {
    if (this.translate.lang === "sv") { return s; }
    if (s.startsWith("Kassakvitto")) {
      return this.getString("Kvitto") + " " + s.substring(11);
    } else {
      return this.getString(s);
    }
  }

  back() {
    //this.urlHistory.navigateBack("/menu/food");
    this.router.navigate([this.appStateService.venueId, "menu", "food"]);
  }

  showOlderEvents() {
    if (this.showLatest) {
      this.showLatest = false;
      this.show6Hour = true;
    } else if (this.show6Hour) {
      this.show6Hour = false;
    }
    this.events = this.processEvents(this.allEvents!, this.showLatest, this.show6Hour);
    console.log("showOlderEvents");
  }

  callForWaitress() {
    this.modalService.show(CallWaitressDialogComponent, {
      data: {
        title: this.getString('Kalla på servis?'),
        message: this.getString('Personal kommer och hjälper dig.'),
        confirmText: this.getString('Bekräfta'),
        abortText: this.getString('Avbryt'),
        action: 'callWaitress'
      }
    });
  }

  navLivesplit() {
    this.router.navigate([this.appStateService.venueId, "livesplit"]);
  }

  openPrebookBooking() {
    this.router.navigate([this.appStateService.venueId, "booking", this.prebookBooked!.booking_ref]);
  }

  navMenu() {
    this.router.navigate([this.appStateService.venueId, "menu", "food"]);
  }

  changeTable() {
    this.modalService.show(WebCodeModalComponent, {
      data: {}
    });
  }

  changeRoom() {
    this.modalService.show(RoomCodeModalComponent, {
      data: {}
    });
  }

  isKioskModeActive(): boolean {
    return this.auth.isKioskMode();
  }

}
